import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  assignSupportTicket,
  changeSupportTicketPriority,
  changeSupportTicketStatus,
  closeSupportTicket,
  createSupportTicket,
  fetchSupportTicketDetail,
  fetchSupportTicketMessages,
  fetchSupportTickets,
  fetchSupportUnreadCount,
  initSupportMessageAttachment,
  initSupportTicketDraftAttachment,
  markSupportTicketRead,
  reopenSupportTicket,
  sendSupportMessage,
} from '@/api/support/supportApi';
import type {
  SupportAttachmentInit,
  SupportAttachmentInitPayload,
  SupportMessage,
  SupportScope,
  SupportTicket,
  SupportTicketCreatePayload,
  SupportTicketDetail,
  SupportTicketFilters,
} from '@/api/support/supportApi.types';
import type { PaginatedResult } from '@/api/pagination.types';
import {
  buildSupportMessagesQueryKey,
  buildSupportTicketDetailQueryKey,
  buildSupportTicketsQueryKey,
  buildSupportUnreadCountQueryKey,
  SUPPORT_TICKETS_QUERY_KEY,
  SUPPORT_TICKET_DETAIL_QUERY_KEY,
  SUPPORT_UNREAD_COUNT_QUERY_KEY,
} from '@/constants/queryKeys';
import {
  SUPPORT_ACTIVE_TICKET_REFETCH_INTERVAL_MILLISECONDS,
  SUPPORT_LIST_REFETCH_INTERVAL_MILLISECONDS,
} from '@/constants/support';
import { useAuth } from '@/hooks/useAuth';

export interface SupportMessageWithSendStatus extends SupportMessage {
  sendStatus: 'sent' | 'sending' | 'failed';
}

export interface SendSupportMessageInput {
  tempId: string;
  body: string | null;
  attachmentIds: string[];
}

function toSentMessage(message: SupportMessage): SupportMessageWithSendStatus {
  return { ...message, sendStatus: 'sent' };
}

export function useSupportTicketsQuery(
  scope: SupportScope,
  page: number,
  filters: SupportTicketFilters
): UseQueryResult<PaginatedResult<SupportTicket>, Error> {
  const { status } = useAuth();
  return useQuery({
    queryKey: buildSupportTicketsQueryKey(
      scope,
      page,
      filters.search,
      filters.status,
      filters.assigneeUserId,
      filters.unassigned
    ),
    queryFn: function loadTickets() {
      return fetchSupportTickets(scope, page, filters);
    },
    enabled: status === 'authenticated',
    placeholderData: keepPreviousData,
    refetchInterval: SUPPORT_LIST_REFETCH_INTERVAL_MILLISECONDS,
  });
}

export function useSupportTicketDetailQuery(
  scope: SupportScope,
  ticketId: string | null
): UseQueryResult<SupportTicketDetail, Error> {
  const { status } = useAuth();
  return useQuery({
    queryKey: buildSupportTicketDetailQueryKey(scope, ticketId === null ? '' : ticketId),
    queryFn: function loadDetail() {
      if (ticketId === null) {
        throw new Error('A ticket must be selected.');
      }
      return fetchSupportTicketDetail(scope, ticketId);
    },
    enabled: ticketId !== null && status === 'authenticated',
    refetchInterval: SUPPORT_ACTIVE_TICKET_REFETCH_INTERVAL_MILLISECONDS,
    refetchIntervalInBackground: false,
  });
}

export function useSupportUnreadCountQuery(scope: SupportScope): UseQueryResult<number, Error> {
  const { status } = useAuth();
  return useQuery({
    queryKey: buildSupportUnreadCountQueryKey(scope),
    queryFn: function loadUnreadCount() {
      return fetchSupportUnreadCount(scope);
    },
    enabled: status === 'authenticated',
    refetchInterval: SUPPORT_LIST_REFETCH_INTERVAL_MILLISECONDS,
  });
}

export function useSupportMessagesQuery(
  scope: SupportScope,
  ticketId: string | null
): UseQueryResult<SupportMessageWithSendStatus[], Error> {
  const queryClient = useQueryClient();
  const { status } = useAuth();
  return useQuery({
    queryKey: buildSupportMessagesQueryKey(scope, ticketId === null ? '' : ticketId),
    queryFn: async function loadMessages() {
      if (ticketId === null) {
        return [];
      }
      const cachedMessages = queryClient.getQueryData<SupportMessageWithSendStatus[]>(
        buildSupportMessagesQueryKey(scope, ticketId)
      );
      const confirmedMessages =
        cachedMessages === undefined
          ? []
          : cachedMessages.filter(function isConfirmed(message) {
              return message.sendStatus === 'sent';
            });
      const lastConfirmedMessage = confirmedMessages[confirmedMessages.length - 1];
      const fetchedMessages = await fetchSupportTicketMessages(scope, ticketId, {
        afterId: lastConfirmedMessage === undefined ? undefined : lastConfirmedMessage.id,
      });
      if (confirmedMessages.length === 0) {
        return fetchedMessages.map(toSentMessage);
      }
      const knownMessageIds = new Set(
        confirmedMessages.map(function readId(message) {
          return message.id;
        })
      );
      const newlyFetchedMessages = fetchedMessages
        .filter(function isUnknown(message) {
          return knownMessageIds.has(message.id) === false;
        })
        .map(toSentMessage);
      const pendingMessages =
        cachedMessages === undefined
          ? []
          : cachedMessages.filter(function isPending(message) {
              return message.sendStatus !== 'sent';
            });
      return [...confirmedMessages, ...newlyFetchedMessages, ...pendingMessages];
    },
    enabled: ticketId !== null && status === 'authenticated',
    refetchInterval: SUPPORT_ACTIVE_TICKET_REFETCH_INTERVAL_MILLISECONDS,
    refetchIntervalInBackground: false,
  });
}

export function useCreateSupportTicketMutation(): UseMutationResult<
  SupportTicketDetail,
  Error,
  SupportTicketCreatePayload
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSupportTicket,
    onSuccess: function refresh() {
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SUPPORT_UNREAD_COUNT_QUERY_KEY });
    },
  });
}

export function useSendSupportMessageMutation(
  scope: SupportScope,
  ticketId: string | null,
  ownSenderKind: string
): UseMutationResult<SupportMessage, Error, SendSupportMessageInput> {
  const queryClient = useQueryClient();
  const messagesQueryKey = buildSupportMessagesQueryKey(scope, ticketId === null ? '' : ticketId);
  return useMutation({
    mutationFn: async function send(input: SendSupportMessageInput) {
      if (ticketId === null) {
        throw new Error('A ticket must be selected before sending a message.');
      }
      return sendSupportMessage(scope, ticketId, input.body, input.attachmentIds);
    },
    onMutate: function applyOptimisticMessage(input: SendSupportMessageInput) {
      const optimisticMessage: SupportMessageWithSendStatus = {
        id: input.tempId,
        ticketId: ticketId === null ? '' : ticketId,
        senderKind: ownSenderKind,
        senderName: null,
        body: input.body,
        attachments: [],
        createdAt: new Date().toISOString(),
        sendStatus: 'sending',
      };
      queryClient.setQueryData<SupportMessageWithSendStatus[]>(
        messagesQueryKey,
        function upsertOptimistic(previousMessages) {
          const withoutSameTempId = (previousMessages === undefined ? [] : previousMessages).filter(
            function isDifferent(message) {
              return message.id !== input.tempId;
            }
          );
          return [...withoutSameTempId, optimisticMessage];
        }
      );
    },
    onError: function markFailed(_sendError, input: SendSupportMessageInput) {
      queryClient.setQueryData<SupportMessageWithSendStatus[]>(
        messagesQueryKey,
        function markMessageFailed(previousMessages) {
          if (previousMessages === undefined) {
            return previousMessages;
          }
          return previousMessages.map(function updateStatus(message) {
            return message.id === input.tempId ? { ...message, sendStatus: 'failed' } : message;
          });
        }
      );
    },
    onSuccess: function reconcile(serverMessage: SupportMessage, input: SendSupportMessageInput) {
      queryClient.setQueryData<SupportMessageWithSendStatus[]>(
        messagesQueryKey,
        function replaceOptimistic(previousMessages) {
          const kept = (previousMessages === undefined ? [] : previousMessages).filter(
            function isKept(message) {
              return message.id !== input.tempId && message.id !== serverMessage.id;
            }
          );
          return [...kept, toSentMessage(serverMessage)];
        }
      );
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SUPPORT_UNREAD_COUNT_QUERY_KEY });
    },
  });
}

export function useMarkSupportTicketReadMutation(
  scope: SupportScope,
  ticketId: string | null
): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async function markRead() {
      if (ticketId === null) {
        return;
      }
      await markSupportTicketRead(scope, ticketId);
    },
    onSuccess: function refresh() {
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SUPPORT_UNREAD_COUNT_QUERY_KEY });
    },
  });
}

export function useInitSupportDraftAttachmentMutation(): UseMutationResult<
  SupportAttachmentInit,
  Error,
  SupportAttachmentInitPayload
> {
  return useMutation({ mutationFn: initSupportTicketDraftAttachment });
}

export function useInitSupportMessageAttachmentMutation(
  scope: SupportScope,
  ticketId: string | null
): UseMutationResult<SupportAttachmentInit, Error, SupportAttachmentInitPayload> {
  return useMutation({
    mutationFn: async function initUpload(payload: SupportAttachmentInitPayload) {
      if (ticketId === null) {
        throw new Error('A ticket must be selected before attaching a file.');
      }
      return initSupportMessageAttachment(scope, ticketId, payload);
    },
  });
}

function useTicketDetailMutation(
  scope: SupportScope,
  ticketId: string,
  mutationFn: () => Promise<SupportTicketDetail>
): UseMutationResult<SupportTicketDetail, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: function refresh(detail: SupportTicketDetail) {
      queryClient.setQueryData(buildSupportTicketDetailQueryKey(scope, ticketId), detail);
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKET_DETAIL_QUERY_KEY });
    },
  });
}

export function useAssignSupportTicketMutation(
  ticketId: string
): UseMutationResult<SupportTicketDetail, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function assign(assigneeUserId: string) {
      return assignSupportTicket(ticketId, assigneeUserId);
    },
    onSuccess: function refresh(detail: SupportTicketDetail) {
      queryClient.setQueryData(buildSupportTicketDetailQueryKey('platform', ticketId), detail);
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKET_DETAIL_QUERY_KEY });
    },
  });
}

export interface ChangeSupportStatusInput {
  status: string;
  note: string | null;
}

export function useChangeSupportStatusMutation(
  ticketId: string
): UseMutationResult<SupportTicketDetail, Error, ChangeSupportStatusInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function changeStatus(input: ChangeSupportStatusInput) {
      return changeSupportTicketStatus(ticketId, input.status, input.note);
    },
    onSuccess: function refresh(detail: SupportTicketDetail) {
      queryClient.setQueryData(buildSupportTicketDetailQueryKey('platform', ticketId), detail);
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKET_DETAIL_QUERY_KEY });
    },
  });
}

export function useChangeSupportPriorityMutation(
  ticketId: string
): UseMutationResult<SupportTicketDetail, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function changePriority(priority: string) {
      return changeSupportTicketPriority(ticketId, priority);
    },
    onSuccess: function refresh(detail: SupportTicketDetail) {
      queryClient.setQueryData(buildSupportTicketDetailQueryKey('platform', ticketId), detail);
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: SUPPORT_TICKET_DETAIL_QUERY_KEY });
    },
  });
}

export function useReopenSupportTicketMutation(
  ticketId: string
): UseMutationResult<SupportTicketDetail, Error, void> {
  return useTicketDetailMutation('practitioner', ticketId, function reopen() {
    return reopenSupportTicket(ticketId);
  });
}

export function useCloseSupportTicketMutation(
  ticketId: string
): UseMutationResult<SupportTicketDetail, Error, void> {
  return useTicketDetailMutation('practitioner', ticketId, function close() {
    return closeSupportTicket(ticketId);
  });
}
